import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import type { LOAN_REQUEST_OFFER_SOURCE, OFFER_DOCUMENT_UPLOAD_STATUS } from 'cpool-constants';
import {
    AGGREGATE_NAMES,
    LOAN_REQUEST_COMMANDS,
    LOAN_REQUEST_OFFER_SOURCES,
    LOAN_REQUEST_RESOLVERS,
    OFFER_DOCUMENT_UPLOAD_STATUSES,
} from 'cpool-constants';
import {
    schemaLoanRequestResolver_GetAcknowledgedDocuments,
    schemaLoanRequestResolver_GetOfferDocumentsUploadData,
} from 'cpool-tables/backend-resolvers';
import { sleep } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator.ts';
import { type SchemaGetOfferDocumentsUploadData } from '~/features/leadDetails/components/Overview/OfferManagement/schemas/getOfferDocumentsUploadData.schema.ts';
import useLead from '~/features/leadDetails/hooks/useLead';
import { fetcher, useAggregateQuery } from '~/queryUtils/useQuery.ts';
import { setQueryPosition, useQueryPositionStore } from '~/store/queryPositionStore.ts';
import { queryKeys } from '~/utils/queryKeys.ts';

export const useOfferDocumentsUpload = ({
    applicationId,
    offerId,
    processId,
    retries = 15,
    source,
    waitTime = 1000,
}: {
    applicationId: string | null | undefined;
    offerId: string;
    processId: string | null;
    retries?: number;
    source: LOAN_REQUEST_OFFER_SOURCE;
    waitTime?: number;
}) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const queryKey = queryKeys.getAcknowledgedDocuments(leadId);
    const queryPosition = useQueryPositionStore((state) => state.positions[JSON.stringify(queryKey)]);

    const acknowledgedDocumentsQuery = useAggregateQuery({
        acl: { loanRequestId: leadId },
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getAcknowledgedDocuments,
        schema: schemaLoanRequestResolver_GetAcknowledgedDocuments,
        searchParams: { id: leadId, position: queryPosition },
    });

    const uploadDataQuery = useAggregateQuery({
        acl: { loanRequestId: leadId },
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getOfferDocumentsUploadData,
        schema: schemaLoanRequestResolver_GetOfferDocumentsUploadData,
        searchParams: { documents: [], id: leadId, source },
    });

    const uploadConfirmedDocuments = async () => {
        const uploadData = uploadDataQuery.data;
        const acknowledgedDocuments = acknowledgedDocumentsQuery.data;
        if (uploadData?.url) {
            if (applicationId) {
                // this will remove the last part of the string and keep the first and middle part
                const applicationNumber = applicationId.split('/').slice(0, -1).join('/');
                window.open(`${uploadData.url}&antragsNummer=${applicationNumber}`, '_blank');
            } else {
                window.open(uploadData.url, '_blank');
            }
        }
        if (!acknowledgedDocuments?.length) {
            toast.info(t('OFFER_MANAGEMENT.TOASTS.DOCUMENT_UPLOAD_NO_ACKNOWLEDGED_FILES'));
            return;
        }
        if (source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE || source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE_SYNC) {
            if (processId == null) {
                toast.error(t('OFFER_MANAGEMENT.TOASTS.DOCUMENT_UPLOAD_MISSING_EUROPACE_PARTNER_ID'));
                return;
            }

            const filteredDocuments = acknowledgedDocuments.filter((document) => {
                if (!lead?.europace?.platform) return false;
                return !document.uploadedTo?.europacePlatform?.[lead.europace.platform];
            });

            if (filteredDocuments.length === 0) {
                toast.info(t('OFFER_MANAGEMENT.TOASTS.DOCUMENT_UPLOAD_ALREADY_UPLOADED'));
                return;
            }

            const response = await APICommunicator.sendCommand({
                aggregateId: leadId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.requestOfferDocumentsUpload,
                payload: {
                    europace: {
                        documents: filteredDocuments.map((document) => ({
                            fileName: document.name,
                            key: document.key,
                        })),
                        offerId,
                        processId,
                    },
                    source,
                },
            });

            if (response.position) {
                // reset getAcknowledgedDocuments with new position
                setQueryPosition(JSON.stringify(queryKey), response.position);

                let requestOffersState: OFFER_DOCUMENT_UPLOAD_STATUS | null = 'PENDING';
                let counter = 0;
                /* eslint-disable no-await-in-loop */
                while (requestOffersState === 'PENDING' && counter < retries) {
                    await sleep(waitTime);
                    const responseUploadData: SchemaGetOfferDocumentsUploadData = await fetcher({
                        meta: {
                            queryUrl: `/query/loan-request/getOfferDocumentsUploadData?id=${leadId}&source=${source}&documents=${JSON.stringify(
                                filteredDocuments.map((document) => document.key),
                            )}`,
                            schema: schemaLoanRequestResolver_GetOfferDocumentsUploadData,
                        },
                    });
                    if (response) {
                        requestOffersState = responseUploadData.uploadStatus;
                    }
                    counter++;
                }
                /* eslint-enable no-await-in-loop */
                // after loop is finished we can refresh the getRecordedDocuments and show error toasts if necessary
                await queryClient.invalidateQueries({ queryKey: queryKeys.getRecordedDocuments(leadId) });
                if (requestOffersState === OFFER_DOCUMENT_UPLOAD_STATUSES.FAILED) {
                    toast.error(t('OFFER_MANAGEMENT.TOASTS.DOCUMENT_UPLOAD_FAILED'));
                } else if (requestOffersState === OFFER_DOCUMENT_UPLOAD_STATUSES.SUCCESS) {
                    toast.success(t('OFFER_MANAGEMENT.TOASTS.DOCUMENT_UPLOAD_SUCCESS'));
                } else if (counter === retries) {
                    toast.error(
                        t('OFFER_MANAGEMENT.TOASTS.DOCUMENT_UPLOAD_TIMEOUT', {
                            count: (retries * waitTime) / 1000,
                        }),
                    );
                }
            } else {
                toast.error(t('OFFER_MANAGEMENT.TOASTS.DOCUMENT_UPLOAD_REQUEST_FAILED'));
            }
        }
    };

    return { acknowledgedDocumentsQuery, uploadConfirmedDocuments, uploadDataQuery };
};
